import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";

export default function AdminSurgicalExcelPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // ------------------------------
  // FETCH ALL ITEMS
  // ------------------------------
  const fetchItems = async () => {
    try {
      const res = await api.get("surgical/list/");
      setItems(res.data || []);
      setFiltered(res.data || []);
      setSelectedIds([]);
      setSelectAll(false);
    } catch (error) {
      console.error(error);
      alert("Failed to load surgical items");
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // ------------------------------
  // IMAGE URL BUILDER
  // ------------------------------
  const getImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith("http")) return img;
    return `http://127.0.0.1:8000${img}`;
  };

  // ------------------------------
  // SEARCH
  // ------------------------------
  const handleSearch = (text) => {
    setSearch(text);

    const q = text.toLowerCase();
    const filteredData = items.filter((item) => {
      return (
        (item.name || "").toLowerCase().includes(q) ||
        (item.company || "").toLowerCase().includes(q) ||
        (item.type || "").toLowerCase().includes(q)
      );
    });

    setFiltered(filteredData);
  };

  // ------------------------------
  // DELETE SINGLE ITEM
  // ------------------------------
  const deleteItem = async (id) => {
    if (!window.confirm("Delete this surgical item?")) return;

    try {
      await api.delete(`surgical/${id}/delete/`);
      fetchItems();
      alert("Item deleted");
    } catch (error) {
      console.error(error);
      alert("Failed to delete item");
    }
  };

  // ------------------------------
  // UPDATE IMAGE
  // ------------------------------
const updateImage = async (id, file) => {
  if (!file || !id) return;

  const formData = new FormData();
  formData.append("image", file);

  try {
    await api.put(
      `/surgical/${id}/update-image/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    alert("Image updated successfully");
    fetchItems(); // refresh list
  } catch (err) {
    console.error(err.response?.data || err);
    alert("Failed to update image");
  }
};



  // ------------------------------
  // CHECKBOX HANDLERS
  // ------------------------------
  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((x) => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const toggleSelectAll = () => {
    if (!selectAll) {
      setSelectedIds(filtered.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
    setSelectAll(!selectAll);
  };

  // ------------------------------
  // BULK DELETE
  // ------------------------------
  const deleteSelected = async () => {
    if (selectedIds.length === 0)
      return alert("No items selected to delete");

    if (!window.confirm("Delete selected items?")) return;

    try {
      await api.post("surgical/bulk-delete/", { ids: selectedIds });
      alert("Selected items deleted");
      fetchItems();
    } catch (error) {
      console.error(error);
      alert("Failed to delete selected items");
    }
  };

  // ------------------------------
  // DELETE ALL
  // ------------------------------
  const deleteAll = async () => {
    if (!window.confirm("Delete ALL surgical items?")) return;

    try {
      await api.delete("surgical/delete-all/");
      alert("All items deleted");
      fetchItems();
    } catch (err) {
      console.error(err);
      alert("Failed to delete all items");
    }
  };

  // ==============================================================
  // RETURN UI
  // ==============================================================
  return (
    <div style={{ padding: 20 }}>
      {/* BACK BUTTON */}
      <button onClick={() => navigate("/admin")} style={styles.backBtn}>
        ← Back
      </button>

      <h2 style={styles.title}>Surgical Items (Excel Data)</h2>

      {/* ADD NEW */}
      <button style={styles.addBtn} onClick={() => navigate("/admin/surgical")}>
        + Add Surgical Item
      </button>

      {/* SEARCH */}
      <input
        type="text"
        placeholder="Search surgical..."
        value={search}
        onChange={(e) => handleSearch(e.target.value)}
        style={styles.search}
      />

      {/* SELECT ALL / DELETE */}
      <div style={{ marginBottom: 15, display: "flex", gap: 10 }}>
        <label>
          <input
            type="checkbox"
            checked={selectAll}
            onChange={toggleSelectAll}
            style={{ marginRight: 6 }}
          />
          Select All
        </label>

        <button style={styles.deleteSelectedBtn} onClick={deleteSelected}>
          Delete Selected
        </button>

        <button style={styles.deleteAllBtn} onClick={deleteAll}>
          Delete ALL
        </button>
      </div>

      {/* GRID */}
      <div style={styles.grid}>
        {filtered.map((item) => {
          const discount =
            item.mrp && item.selling_price
              ? Math.round(((item.mrp - item.selling_price) / item.mrp) * 100)
              : 0;

          return (
            <div key={item.id} style={styles.card}>
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selectedIds.includes(item.id)}
                onChange={() => toggleSelect(item.id)}
                style={{ marginBottom: 8 }}
              />

              {/* Image */}
              <div
                onClick={() =>
                  document.getElementById(`file-${item.id}`).click()
                }
                style={styles.imageBox}
              >
                {item.image ? (
                  <img
                    src={getImageUrl(item.image)}
                    alt="surgical"
                    style={styles.image}
                  />
                ) : (
                  <span style={{ color: "#555", fontSize: 13 }}>
                    Tap to add image
                  </span>
                )}
              </div>

              <input
                type="file"
                id={`file-${item.id}`}
                style={{ display: "none" }}
                accept="image/*"
                onChange={(e) => updateImage(item.id, e.target.files[0])}
              />

              {/* NAME */}
              <h4 style={{ margin: "6px 0" }}>{item.name}</h4>

              {/* COMPANY + TYPE */}
              <p style={styles.subText}>
                {(item.company || "Unknown company") +
                  (item.type ? ` • ${item.type}` : "")}
              </p>

              {/* PRICES */}
              <p style={styles.priceText}>MRP: <b>₹{item.mrp}</b></p>
              <p style={styles.priceText}>SP: <b>₹{item.selling_price}</b></p>

              {/* DISCOUNT */}
              {discount > 0 && (
                <p style={{ color: "orange", fontWeight: "bold", marginTop: 4 }}>
                  {discount}% OFF
                </p>
              )}

              {/* STOCK */}
              <p
                style={{
                  ...styles.stockText,
                  color: item.stock <= 0 ? "red" : "green",
                }}
              >
                {item.stock <= 0
                  ? "OUT OF STOCK"
                  : `Stock: ${item.stock} pcs`}
              </p>

              {/* BUTTONS */}
              <div style={styles.btnRow}>
                <button
                  style={styles.editBtn}
                  onClick={() => navigate(`/admin/surgical/${item.id}`)}
                >
                  Edit
                </button>

                <button
                  style={styles.deleteBtn}
                  onClick={() => deleteItem(item.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <p style={{ marginTop: 20 }}>No surgical items found.</p>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------
// STYLES
// ----------------------------------------------------------
const styles = {
  backBtn: {
    marginBottom: 10,
    padding: "6px 12px",
    background: "#ddd",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 10,
  },
  addBtn: {
    background: "green",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    marginBottom: 15,
    fontWeight: "bold",
  },
  search: {
    width: "100%",
    padding: 10,
    marginBottom: 20,
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  deleteSelectedBtn: {
    background: "orange",
    padding: "6px 12px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    color: "#fff",
    fontWeight: "bold",
  },
  deleteAllBtn: {
    background: "red",
    padding: "6px 12px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    color: "#fff",
    fontWeight: "bold",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 20,
  },
  card: {
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #eee",
    padding: 12,
    textAlign: "center",
  },
  imageBox: {
    width: "100%",
    height: 130,
    background: "#efefef",
    borderRadius: 8,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  subText: {
    color: "#666",
    fontSize: 13,
    marginBottom: 6,
  },
  priceText: {
    color: "#000",
    fontSize: 14,
    margin: 0,
  },
  stockText: {
    fontSize: 14,
    margin: "6px 0",
    fontWeight: 600,
  },
  btnRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 10,
    gap: 6,
  },
  editBtn: {
    background: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "6px 10px",
    cursor: "pointer",
    flex: 1,
    fontSize: 13,
  },
  deleteBtn: {
    background: "red",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "6px 10px",
    cursor: "pointer",
    flex: 1,
    fontSize: 13,
  },
};
